import { useState } from 'react';
import PropTypes from 'prop-types';

import { Announcement, Button, Input, isMaxNameLength, ModalV2 as Modal, Pill } from '@utilities';

import { createSurveyPeopleGroup } from '@api/people_groups';

import { transformPeopleGroupError } from './utilities/helpers';

import styles from './_index.module.scss';

const ModalSurveyPeopleGroup = ({
  compareBy,
  demographicFilter = {},
  globalFilters,
  optionFilter = [],
  quotaGroupFilter = [],
  sampleSize,
  setShowModalSurveyPeopleGroup,
  surveyId,
  surveyQuestions,
  surveyTitle,
  totalResponses,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState(surveyTitle);
  const [promptErrors, setPromptErrors] = useState([]);

  // hydrate selected filters with labels from global filters
  const demographicFiltersWithLabels = Object.keys(demographicFilter).map((key) => {
    const globalFilter = globalFilters?.demographicFilters.find(({ id }) => id === key);
    return { id: key, label: globalFilter?.label || key, values: demographicFilter[key] };
  });

  const optionFilterLabels = surveyQuestions?.reduce((acc, question) => {
    const { options, title: questionTitle } = question;
    if (!options || options.length === 0) return acc;
    options.forEach((option) => {
      const { id: optionId, title: optionTitle } = option;
      acc[optionId] = {
        optionId,
        optionTitle,
        questionId: question.id,
        questionTitle,
      };
    });
    return acc;
  }, {});

  const optionFilterQuestionMap = optionFilter.reduce((acc, optionId) => {
    const option = optionFilterLabels[optionId];
    if (!option) return acc;
    const { optionTitle, questionId, questionTitle } = option;
    if (!acc[questionId]) {
      acc[questionId] = {
        options: [],
        questionTitle,
      };
    }
    acc[questionId].options.push({
      optionId,
      optionTitle,
    });
    return acc;
  }, {});

  const optionFilterQuestionList = Object.entries(optionFilterQuestionMap).map(
    ([questionId, { options, questionTitle }]) => ({
      options,
      questionId,
      questionTitle,
    })
  );

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setPromptErrors([]);

      await createSurveyPeopleGroup({
        compareBy,
        demographicFilter,
        name,
        optionFilter,
        quotaGroupFilter: quotaGroupFilter?.flatMap(({ quotaGroupRefs }) => quotaGroupRefs),
        sampleSize,
        surveyId,
      });

      setShowModalSurveyPeopleGroup(false);
    } catch (error) {
      const data = transformPeopleGroupError(error?.response?.data);
      setPromptErrors(data);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      buttons={[
        <Button
          data-testid="cancel-button"
          key="cancel-btn"
          onClick={() => setShowModalSurveyPeopleGroup(false)}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          data-testid="submit-button"
          isDisabled={!name?.trim() || isMaxNameLength(name)}
          isLoading={isSubmitting}
          key="submit-btn"
          onClick={handleSubmit}
          text="Save"
        />,
      ]}
      className={styles['modal-survey-people-group']}
      isActive
      onClose={() => setShowModalSurveyPeopleGroup(false)}
      title="Create Survey People Group"
    >
      <div className={styles['modal-survey-people-group-content']}>
        {promptErrors && Array.isArray(promptErrors) && promptErrors?.length > 0 && (
          <Announcement text={promptErrors[0]} variant="error" />
        )}
        <Input
          data-testid="survey-people-group-name"
          error={
            isMaxNameLength(name)
              ? 'Maximum number of characters reached. Try something shorter.'
              : ''
          }
          label="Name"
          onChange={setName}
          value={name}
        />

        <div>
          <h4>Sample Size</h4>
          <p>{`${sampleSize === totalResponses ? totalResponses : `${sampleSize} / ${totalResponses}`} available respondents`}</p>
        </div>

        {sampleSize < 50 && (
          <Announcement
            text="Warning. The Survey People Group you are creating has a low sample size."
            variant="warn"
          />
        )}

        {demographicFiltersWithLabels.length > 0 && (
          <div>
            <h4>Demographic Filters</h4>
            <ul className={styles['modal-survey-people-group-pill-list']}>
              {demographicFiltersWithLabels.map(({ id, label, values }) =>
                values.map((value, index) => (
                  <li key={`demographic-filter-${id}-${index}`}>
                    <Pill isCompleted value={`${label}: ${value}`} />
                  </li>
                ))
              )}
            </ul>
          </div>
        )}

        {optionFilterQuestionList.length > 0 && (
          <div>
            <h4>Question Filters</h4>
            {optionFilterQuestionList.map(({ options, questionId, questionTitle }) => (
              <div
                className={styles['modal-survey-people-group-question-filter']}
                key={`question-filter-${questionId}`}
              >
                <strong>{questionTitle}</strong>
                <ul className={styles['modal-survey-people-group-question-list']}>
                  {options.map(({ optionId, optionTitle }) => (
                    <li key={`option-filter-${optionId}`}>{optionTitle}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {quotaGroupFilter.length > 0 && (
          <div>
            <h4>Quota Group Filters</h4>
            <ul className={styles['modal-survey-people-group-pill-list']}>
              {quotaGroupFilter.map(({ instantSurveyGroupRef, name }) => (
                <li key={`quota-group-filter-${instantSurveyGroupRef}`}>
                  <Pill isCompleted value={name} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

ModalSurveyPeopleGroup.propTypes = {
  compareBy: PropTypes.string,
  demographicFilter: PropTypes.object,
  globalFilters: PropTypes.object,
  optionFilter: PropTypes.array,
  quotaGroupFilter: PropTypes.array,
  sampleSize: PropTypes.number,
  setShowModalSurveyPeopleGroup: PropTypes.func,
  surveyId: PropTypes.number,
  surveyQuestions: PropTypes.array,
  surveyTitle: PropTypes.string,
  totalResponses: PropTypes.number,
};

export default ModalSurveyPeopleGroup;
